import React, { useEffect, useState, useRef } from 'react';
import { Space, Table, Button, Input, Modal, message } from 'antd';
import {
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';

import IconGroup from './IconGroup';
import CreateModalContent from './CreateModalContent';
import { LoadingIcon } from '../../../components';
import {
  getCrops,
  putCrop,
  putCropStatus,
  postCrop,
  deleteCrop,
} from '../../../utils/api';

const CropTable = () => {
  const [data, setData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const searchInput = useRef(null);

  const showCreateModal = () => {
    setCreateModalOpen(true);
  };

  const handleCreateCancel = () => {
    setCreateModalOpen(false);
  };

  const renderBoolean = (bool) => (
    <p className={`${bool ? 'text-primaryBlue' : 'text-dangerRed'}`}>
      {String(bool)}
    </p>
  );

  // START - API Functions
  const fetchCrops = async () => {
    try {
      setDataLoading(true);
      const res = await getCrops();
      const newData = res.data.data.map((crop) => {
        return {
          ...crop,
          key: crop.id,
        };
      });
      setData(newData);
    } catch (err) {
      message.error('Error fetching Crops!');
    } finally {
      setDataLoading(false);
    }
  };

  const updateCrop = async (id, data) => {
    try {
      const res = await putCrop(id, data);
      const res2 = await fetchCrops();
      console.log({ res });
    } catch (err) {
      message.error('Error updating Crop!');
    }
  };

  const hideCrop = async (id, data) => {
    try {
      const res = await putCropStatus(id, data);
      const res2 = await fetchCrops();
      console.log({ res });
    } catch (err) {
      message.error('Error hiding Crop!');
    }
  };

  const createCrop = async (data) => {
    try {
      const res = await postCrop(data);
      const res2 = await fetchCrops();
      console.log({ res });
    } catch (err) {
      message.error('Error creating Crop!');
    }
  };

  const removeCrop = async (id) => {
    try {
      const res = await deleteCrop(id);
      const res2 = await fetchCrops();
      console.log({ res });
    } catch (err) {
      message.error('Error deleting Crop!');
    }
  };
  // END - API Functions

  // Fetch orgs on component mount
  useState(() => {
    fetchCrops();
  }, []);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
    },
    {
      title: 'Icon',
      dataIndex: 'icon',
      key: 'maxSite',
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'active',
      render: (value) =>
        !!value ? (
          <CheckCircleOutlined className='text-successGreen' />
        ) : (
          <CloseCircleOutlined className='text-dangerRed' />
        ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      render: (_, record) => (
        <IconGroup
          ele={record}
          updateCallback={updateCrop}
          hideCallback={hideCrop}
          deleteCallback={removeCrop}
        />
      ),
    },
  ];

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size='small'
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size='small'
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => {
              confirm({
                closeDropdown: true,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => {
              close();
            }}
          >
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#39f' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const finalColumns = columns.map((vals) => {
    const obj = { ...vals, ...getColumnSearchProps(vals.key) };
    return obj;
  });

  return (
    <div>
      {dataLoading ? (
        <LoadingIcon classname='absolute h-full top-[40vh]' height="200px" />
      ) : (
        <>
          <div className='w-full pb-10 h-fit flex justify-end sm:justify-start items-center'>
            <Button type='primary' onClick={() => setCreateModalOpen(true)}>
              Create Crop
            </Button>
          </div>
          <Table
            dataSource={data}
            columns={finalColumns}
            pagination={{
              hideOnSinglePage: true,
              position: ['bottomCenter'],
            }}
          />
        </>
      )}
      <Modal
        maskClosable={false}
        title={
          <div className='mb-5 font-IBMPlexSansBold text-2xl'>Create Crop</div>
        }
        open={createModalOpen}
        onCancel={handleCreateCancel}
        footer={null}
      >
        <CreateModalContent
          setOpen={setCreateModalOpen}
          createCallback={createCrop}
        />
      </Modal>
    </div>
  );
};

export default CropTable;
